import React, { useMemo } from 'react';
import { Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import DataTable from '../../../../ui/Table/DataTable';
import type { CompositionEditorDisplayRow, CompositionRowAction } from '../../lib/compositionRowActions';
import CompositionRow from './CompositionRow';

export type CompositionTableProps = {
  rows: CompositionEditorDisplayRow[];
  emptyDescription: string;
  emptyExtra?: React.ReactNode;
  loading?: boolean;
  onRowAction: (row: CompositionEditorDisplayRow, action: CompositionRowAction) => void;
  actionLoadingRowKey?: string | null;
};

function rowCellProps(
  record: CompositionEditorDisplayRow,
  onRowAction: CompositionTableProps['onRowAction'],
  actionLoadingRowKey: CompositionTableProps['actionLoadingRowKey']
) {
  return {
    documentLabel: record.documentLabel,
    required: record.required,
    state: record.state,
    compositionStatus: record.compositionStatus,
    source: record.source,
    isManual: record.isManual,
    fileName: record.fileName,
    documentTypeLabel: record.documentTypeLabel,
    routeLabel: record.routeLabel,
    availableActions: record.availableActions,
    onRowAction: (action: CompositionRowAction) => onRowAction(record, action),
    actionLoading: actionLoadingRowKey === record.rowKey,
  };
}

const CompositionTable: React.FC<CompositionTableProps> = ({
  rows,
  emptyDescription,
  emptyExtra,
  loading = false,
  onRowAction,
  actionLoadingRowKey = null,
}) => {
  const columns: ColumnsType<CompositionEditorDisplayRow> = useMemo(
    () => [
      {
        title: 'Документ',
        dataIndex: 'documentLabel',
        key: 'document',
        width: 220,
        className: 'primary-cell',
        ellipsis: true,
        render: (_value, record) => (
          <CompositionRow column="document" {...rowCellProps(record, onRowAction, actionLoadingRowKey)} />
        ),
      },
      {
        title: 'Тип',
        dataIndex: 'documentTypeLabel',
        key: 'documentTypeLabel',
        width: 150,
        ellipsis: true,
        render: (_value, record) => (
          <CompositionRow column="type" {...rowCellProps(record, onRowAction, actionLoadingRowKey)} />
        ),
      },
      {
        title: 'Статус',
        dataIndex: 'state',
        key: 'status',
        width: 125,
        render: (_value, record) => (
          <CompositionRow column="status" {...rowCellProps(record, onRowAction, actionLoadingRowKey)} />
        ),
      },
      {
        title: 'Файл',
        dataIndex: 'fileName',
        key: 'fileName',
        width: 220,
        ellipsis: true,
        render: (_value, record) => (
          <CompositionRow column="file" {...rowCellProps(record, onRowAction, actionLoadingRowKey)} />
        ),
      },
      {
        title: 'Маршрут',
        dataIndex: 'routeLabel',
        key: 'routeLabel',
        width: 180,
        ellipsis: true,
        render: (_value, record) => (
          <CompositionRow column="route" {...rowCellProps(record, onRowAction, actionLoadingRowKey)} />
        ),
      },
      {
        title: 'Обяз.',
        dataIndex: 'required',
        key: 'required',
        width: 74,
        align: 'center',
        render: (_value, record) => (
          <CompositionRow column="required" {...rowCellProps(record, onRowAction, actionLoadingRowKey)} />
        ),
      },
      {
        title: 'Действия',
        key: 'actions',
        width: 170,
        align: 'right',
        render: (_value, record) => (
          <CompositionRow column="actions" {...rowCellProps(record, onRowAction, actionLoadingRowKey)} />
        ),
      },
    ],
    [onRowAction, actionLoadingRowKey]
  );

  return (
    <DataTable<CompositionEditorDisplayRow>
      size="small"
      rowKey="rowKey"
      columns={columns}
      dataSource={rows}
      loading={loading}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 1139, y: 'clamp(280px, 42vh, 540px)' }}
      className="cp-composition__table"
      rowClassName={(record) =>
        record.compositionStatus === 'EXCLUDED' ? 'cp-composition__row--excluded' : ''
      }
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div className="cp-composition__empty-content">
                <span>{emptyDescription}</span>
                {emptyExtra}
              </div>
            }
            style={{ margin: '20px 0' }}
          />
        ),
      }}
    />
  );
};

export default CompositionTable;
